import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsBelow } from '@/hooks/useBreakpoint';
import { useFieldLocked } from './FieldLock';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ComboboxOption<T = string | number> {
  value: T;
  label: string;
  meta?: string;
  group?: string;
  disabled?: boolean;
  keywords?: string;
}

interface BaseProps<T> {
  label?: string;
  placeholder?: string;
  options: ComboboxOption<T>[];
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  wrapperClassName?: string;
  emptyMessage?: string;
  id?: string;
  /** When true, only the option label is searched (group, meta, keywords are excluded from filtering). */
  searchByLabelOnly?: boolean;
  /**
   * Open the panel in a portal (default). Pass false inside a v5 Radix Dialog / Sheet:
   * its focus trap and outside-pointer block ignore a portalled panel from the
   * separate Radix copy (see v5 PopoverContent portal={false}); in place, the
   * dialog's transform still keeps it from being clipped.
   */
  portal?: boolean;
}

interface SingleProps<T> extends BaseProps<T> {
  mode?: 'single';
  value: T | null;
  onChange: (value: T | null) => void;
  clearable?: boolean;
}

interface MultiAddProps<T> extends BaseProps<T> {
  mode: 'multi-add';
  onAdd: (option: ComboboxOption<T>) => void;
  /** Ids (option values) that are already selected — rendered disabled */
  addedValues?: T[];
  /**
   * Keep `addedValues` options selectable: they still show the "added" badge,
   * but can be added again. For lists where the same value may repeat.
   */
  allowRepeat?: boolean;
  /** Close the dropdown after adding an item (default: false, stays open) */
  closeOnAdd?: boolean;
}

export type SearchableComboboxProps<T = string | number> = SingleProps<T> | MultiAddProps<T>;

// ─── Component ────────────────────────────────────────────────────────────────

export function SearchableCombobox<T extends string | number = string | number>(
  props: SearchableComboboxProps<T>,
) {
  const {
    label,
    placeholder = 'Search…',
    options,
    loading = false,
    disabled = false,
    required,
    error,
    hint,
    className,
    wrapperClassName,
    emptyMessage = 'No matches',
    id,
    searchByLabelOnly = false,
    portal = true,
  } = props;

  const isMulti = props.mode === 'multi-add';
  const addedValues: T[] = isMulti ? (props as MultiAddProps<T>).addedValues ?? [] : [];
  const allowRepeat = isMulti && (props as MultiAddProps<T>).allowRepeat === true;

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  /** Touch tier. Drives outside-dismissal and whether a search field autofocuses. */
  const isTouchTier = useIsBelow('md');
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  /**
   * Inside a locked FieldLockScope a single select shows its value as plain
   * content (never opens; no chevron or clear; not drawn disabled), and a
   * multi-add picker, which only adds, is left out.
   */
  const locked = useFieldLocked();

  const inputId = id ?? (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const errorId = error ? `${inputId}-error` : undefined;

  const selectedOption = !isMulti
    ? options.find((o) => o.value === (props as SingleProps<T>).value) ?? null
    : null;

  // ─── Filter + group ───────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => {
      const hay = searchByLabelOnly
        ? o.label.toLowerCase()
        : `${o.label} ${o.meta ?? ''} ${o.group ?? ''} ${o.keywords ?? ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [options, search, searchByLabelOnly]);

  const grouped = useMemo(() => {
    const map = new Map<string, ComboboxOption<T>[]>();
    for (const opt of filtered) {
      const key = opt.group ?? '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(opt);
    }
    return Array.from(map.entries());
  }, [filtered]);

  // Flat list of selectable options for keyboard nav
  const flatOptions = useMemo(
    () => filtered.filter((o) => !o.disabled && !(isMulti && !allowRepeat && addedValues.includes(o.value))),
    [filtered, isMulti, allowRepeat, addedValues],
  );

  useEffect(() => {
    if (activeIndex >= flatOptions.length) setActiveIndex(0);
  }, [flatOptions.length, activeIndex]);

  // ─── Handlers ─────────────────────────────────────────────────────────
  const commitOption = useCallback(
    (opt: ComboboxOption<T>) => {
      if (opt.disabled) return;
      if (isMulti) {
        if (!allowRepeat && addedValues.includes(opt.value)) return;
        (props as MultiAddProps<T>).onAdd(opt);
        setSearch('');
        setActiveIndex(0);
        if ((props as MultiAddProps<T>).closeOnAdd) {
          setOpen(false);
          // Closing the panel is not enough on a touch device: the search input keeps
          // focus, so the on-screen keyboard stays up covering the cart the user is
          // trying to see, and `onFocus` on that same input is wired to reopen the
          // panel. SCRUM-1411 reported both — "the dropdown stays open and the keyboard
          // stays up … the user has to tap somewhere else before the added product can
          // be seen". Blur only on touch; a keyboard user adding several items in a row
          // needs the focus kept.
          if (isTouchTier) inputRef.current?.blur();
        } else {
          // Keeping the field focused after an add is right with a keyboard and wrong
          // with a touch keyboard covering the list the user is picking from.
          if (!isTouchTier) requestAnimationFrame(() => inputRef.current?.focus());
        }
      } else {
        (props as SingleProps<T>).onChange(opt.value);
        setOpen(false);
        setSearch('');
        if (isTouchTier) inputRef.current?.blur();
      }
    },
    [isMulti, allowRepeat, addedValues, props, isTouchTier],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!open) setOpen(true);
        setActiveIndex((i) => Math.min(i + 1, flatOptions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const opt = flatOptions[activeIndex];
        if (opt) commitOption(opt);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [disabled, open, flatOptions, activeIndex, commitOption],
  );

  // Closing from outside: a press or focus outside the field and the panel (Radix
  // listens on pointerdown, so a tap on a phone dismisses it too).
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) return;
      setOpen(false);
      if (isMulti) setSearch('');
    },
    [isMulti],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isMulti) (props as SingleProps<T>).onChange(null);
    },
    [isMulti, props],
  );

  // ─── Render ───────────────────────────────────────────────────────────

  const showClear =
    !isMulti && (props as SingleProps<T>).clearable && selectedOption && !disabled && !locked;

  // Display string for single-select closed state
  const PanelLayer = portal ? PopoverPrimitive.Portal : React.Fragment;

  const singleDisplay = loading
    ? 'Loading…'
    : selectedOption
      ? selectedOption.label + (selectedOption.meta ? '  —  ' + selectedOption.meta : '')
      : placeholder;

  // A multi-add picker only adds: nothing to show while locked (the page lists what was added).
  if (locked && isMulti) return null;

  return (
    <div className={cn('w-full', wrapperClassName)} ref={containerRef}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
          {required && (
            <span className="ml-0.5 text-red-500" aria-hidden="true">
              {' *'}
            </span>
          )}
        </label>
      )}

      <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Anchor asChild>
      <div className="relative">
        {isMulti ? (
          <div
            className={cn(
              'form-input-base flex items-center gap-2',
              error && 'form-input-error',
              disabled && 'opacity-50 cursor-not-allowed',
              className,
            )}
            onClick={() => !disabled && setOpen(true)}
          >
            <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              value={search}
              disabled={disabled || loading}
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setSearch(e.target.value);
                setOpen(true);
                setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder={loading ? 'Loading…' : placeholder}
              className="flex-1 bg-transparent outline-none text-sm placeholder-slate-400"
              aria-expanded={open}
              aria-controls={`${inputId}-listbox`}
              aria-autocomplete="list"
              role="combobox"
            />
            <ChevronDown
              className={cn(
                'h-4 w-4 text-slate-400 transition-transform flex-shrink-0',
                open && 'rotate-180',
              )}
            />
          </div>
        ) : (
          <button
            type="button"
            id={inputId}
            disabled={!locked && (disabled || loading)}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-invalid={!!error}
            aria-describedby={errorId}
            onClick={() => !locked && !disabled && !loading && setOpen((o) => !o)}
            onKeyDown={locked ? undefined : handleKeyDown}
            className={cn(
              locked
                ? 'w-full flex items-center gap-2 text-left form-input-base cursor-default'
                : 'w-full flex items-center gap-2 text-left form-input-base cursor-pointer',
              error && 'form-input-error',
              !locked && (disabled || loading) && 'cursor-not-allowed',
              className,
            )}
          >
            <span className={cn('flex-1 truncate', !selectedOption && 'text-slate-400')}>
              {singleDisplay}
            </span>
            {!locked && (
              <span className="flex items-center gap-1 flex-shrink-0">
                {showClear && (
                  <X
                    className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600"
                    onClick={handleClear}
                  />
                )}
                <ChevronDown
                  className={cn(
                    'h-4 w-4 text-slate-400 transition-transform duration-150',
                    open && 'rotate-180',
                  )}
                />
              </span>
            )}
          </button>
        )}

      </div>
      </PopoverPrimitive.Anchor>

      {/* The panel opens in a portal, as corp's searchable select does: drawn under
          the field it was clipped by any scrolling parent (a modal's body), and it
          could not move above the field when there was no room below. */}
      <PanelLayer>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          style={{
            minWidth: 'var(--radix-popover-trigger-width)',
            // As corp: as wide as the longest option (never narrower than the field),
            // up to the room on screen; only then does a label wrap. Never cut off.
            maxWidth: 'var(--radix-popover-content-available-width, 90vw)',
            maxHeight: 'var(--radix-popover-content-available-height, 400px)',
          }}
          // The search field focuses itself (not on touch); closing leaves focus alone.
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          // A press on the field itself is not "outside": its own click opens / closes.
          onInteractOutside={(e) => {
            if (containerRef.current?.contains(e.target as Node)) e.preventDefault();
          }}
          // Escape closes this panel only, never an enclosing Modal (as SearchableMultiSelect).
          onEscapeKeyDown={(e) => e.stopPropagation()}
          className={cn(
            'z-50 flex flex-col overflow-hidden rounded-md bg-white border border-slate-200 shadow-lg',
            'animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
          )}
        >
          <div
            id={`${inputId}-listbox`}
            role="listbox"
            aria-label={label}
            className="flex min-h-0 flex-col"
          >
            {!isMulti && (
              <div className="p-2 border-b border-slate-100">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={search}
                    // A touch keyboard opening on top of the list the user is about to
                    // pick from is worse than one extra tap to focus the field.
                    autoFocus={!isTouchTier}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setActiveIndex(0);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            <div className="max-h-72 min-h-0 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <p className="px-3 py-3 text-sm text-slate-400 text-center">{emptyMessage}</p>
              ) : (
                grouped.map(([groupName, opts]) => (
                  <div key={groupName || '__ungrouped__'}>
                    {groupName && (
                      <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">
                        {groupName}
                      </div>
                    )}
                    {opts.map((opt) => {
                      const isSelected =
                        !isMulti && selectedOption?.value === opt.value;
                      const isAlreadyAdded = isMulti && addedValues.includes(opt.value);
                      const isDisabled = opt.disabled || (isAlreadyAdded && !allowRepeat);
                      const flatIdx = flatOptions.indexOf(opt);
                      const isActive = flatIdx === activeIndex && flatIdx >= 0;
                      return (
                        <button
                          key={String(opt.value)}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          disabled={isDisabled}
                          onMouseEnter={() => flatIdx >= 0 && setActiveIndex(flatIdx)}
                          onClick={() => commitOption(opt)}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                            isActive && !isDisabled && 'bg-blue-50',
                            isSelected && 'bg-blue-100 text-blue-700',
                            isDisabled && 'opacity-50 cursor-not-allowed',
                            !isDisabled && !isActive && !isSelected && 'hover:bg-slate-50',
                          )}
                        >
                          <span className="min-w-0 flex-1 break-words" title={opt.label}>
                            {opt.label}
                          </span>
                          {opt.meta && (
                            <span
                              className={cn(
                                'flex-shrink-0 text-xs tabular-nums',
                                isSelected ? 'text-blue-700' : 'text-slate-500',
                              )}
                            >
                              {opt.meta}
                            </span>
                          )}
                          {isSelected && <Check className="h-4 w-4 flex-shrink-0" />}
                          {isAlreadyAdded && (
                            <span className="text-[11px] text-slate-400 flex-shrink-0">added</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        </PopoverPrimitive.Content>
      </PanelLayer>
      </PopoverPrimitive.Root>

      {hint && !error && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
